import {
    Book as BookIcon,
    GridOn as GridIcon,
    Message as MessageIcon,
    Search as SearchIcon
} from "@mui/icons-material";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Chip,
    Grid,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "../../services/AuthService";
import Logger from "../../services/Logger";
import { getContentLanguage, setContentLanguage } from "../../services/LangPrefs";
import HomeService from "../../services/HomeService";
import { trackEvent } from "../../services/GoogleAnalytics";
import ClusteredBubblesMiniChart from "../components/ClusteredBubblesMiniChart";
import ReferralEventSection from "../referral/ReferralEventSection";
import SlidingPlayMistBackground from "../puzzles/sliding/SlidingPlayMistBackground";
import SignUpSection from "./SignUpSection";
import LandingConnectionsCarousel from "./carousel/LandingConnectionsCarousel";
import LandingStoryCarousel from "./carousel/LandingStoryCarousel";
import LandingFeaturedPuzzlesHero from "./LandingFeaturedPuzzlesHero";
import { getLandingWeeklyTheme } from "./landingWeeklyTheme";
import {
    WhyChooseVisualAiAssistant,
    WhyChooseVisualBilingualPuzzles
} from "./WhyChooseCardVisuals";
import { SUPPORT_FRENCH } from "../../config/supportFrench";
import { usePagesTranslation } from "../locales";

/** Set true to restore the landing "Free Puzzles to Get Started" section. */
const SHOW_FREE_PUZZLES_SECTION = false;

/** Set true to restore the landing "Benefits of Solving Puzzles" section. */
const SHOW_BENEFITS_SECTION = true;

/** Set true to restore the landing story carousel above the main content. */
const SHOW_STORY_CAROUSEL = false;

/** Set true to restore the landing connections editorial carousel. */
const SHOW_CONNECTIONS_CAROUSEL = true;

/** Set true to restore the landing "Why Choose Our Puzzles?" section. */
const SHOW_WHY_CHOOSE_SECTION = false;

const Landing = () => {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [authReady, setAuthReady] = useState(false);
    const [freePuzzles, setFreePuzzles] = useState([]);
    const [, setLoading] = useState(true);
    const [contentLang, setContentLang] = useState(() => getContentLanguage());
    const [storyCarouselItems, setStoryCarouselItems] = useState([]);
    const weeklyTheme = useMemo(() => getLandingWeeklyTheme(), []);

    useEffect(() => {
        trackEvent("entered", { name: "Landing", theme: weeklyTheme.id });
    }, [weeklyTheme.id]);

    useEffect(() => {
        if (!SHOW_STORY_CAROUSEL) {
            return undefined;
        }
        let cancelled = false;
        (async () => {
            const data = await HomeService.getStoryCarousel(contentLang);
            if (cancelled) return;
            const list = Array.isArray(data) ? data : [];
            setStoryCarouselItems(list);
        })();
        return () => {
            cancelled = true;
        };
    }, [contentLang]);

    useEffect(() => {
        const getUser = async () => {
            try {
                const isAuthed = await AuthService.isAuthenticated();
                if (isAuthed) {
                    const userInfo = await AuthService.getUser();
                    setUser(userInfo);
                }
            } catch (error) {
                if (error.name !== 'UserUnAuthenticatedException') {
                    Logger.error("Error getting user:", error);
                }
            } finally {
                setAuthReady(true);
            }
        };

        getUser();
    }, []);

    useEffect(() => {
        if (!authReady || !SHOW_FREE_PUZZLES_SECTION) {
            return;
        }

        const loadPuzzles = async () => {
            try {
                const puzzles = await HomeService.getHomePuzzles(contentLang);
                setFreePuzzles(puzzles);
            } catch (error) {
                Logger.error('Error loading puzzles:', error);
            } finally {
                setLoading(false);
            }
        };

        loadPuzzles();
    }, [contentLang, authReady]);

    const handlePuzzleClick = (puzzle) => {
        // For free puzzles, allow access without login
        if (!puzzle.isPremium) {
            // Navigate to crossword puzzle
            if (puzzle.type === 'CROSSWORD') {
                navigate(`/puzzle/play-cross/${puzzle.id}`);
                return;
            }
            // Navigate to grid puzzle (word search or hidden message)
            const gt = String(puzzle.type || "").toUpperCase();
            if (gt === "HIDDEN_MESSAGE") {
                navigate(`/puzzle/play-hidden-message/${puzzle.id}`);
            } else {
                navigate(`/puzzle/play-word-search/${puzzle.id}`);
            }
            return;
        }

        // For premium puzzles, check if user is logged in
        if (!user) {
            // Redirect to login page if not logged in
            const puzzlePath =
                puzzle.type === "CROSSWORD"
                    ? `/puzzle/play-cross/${puzzle.id}`
                    : String(puzzle.type || "").toUpperCase() === "HIDDEN_MESSAGE"
                      ? `/puzzle/play-hidden-message/${puzzle.id}`
                      : `/puzzle/play-word-search/${puzzle.id}`;
            navigate('/login', {
                state: {
                    from: { pathname: puzzlePath },
                    message: t("landing.login-standard-msg")
                }
            });
            return;
        }

        // If logged in, navigate to puzzle
        if (puzzle.type === 'CROSSWORD') {
            navigate(`/puzzle/play-cross/${puzzle.id}`);
        } else {
            const gt = String(puzzle.type || "").toUpperCase();
            if (gt === "HIDDEN_MESSAGE") {
                navigate(`/puzzle/play-hidden-message/${puzzle.id}`);
            } else {
                navigate(`/puzzle/play-word-search/${puzzle.id}`);
            }
        }
    };

    const handleContentLanguageChange = (_, newLang) => {
        if (newLang) {
            setContentLang(newLang);
            setContentLanguage(newLang);
        }
    };

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                minHeight: "100%",
                backgroundColor: "transparent",
            }}
        >
            <Box
                aria-hidden
                sx={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 0,
                    pointerEvents: "none",
                    overflow: "hidden",
                }}
            >
                <SlidingPlayMistBackground theme={weeklyTheme} />
            </Box>

            {SHOW_STORY_CAROUSEL ? (
                <Box sx={{ position: "relative", zIndex: 1 }}>
                    <LandingStoryCarousel
                        items={storyCarouselItems}
                        contentLanguage={contentLang}
                    />
                </Box>
            ) : null}

            <Box sx={{
                position: "relative",
                zIndex: 1,
                maxWidth: 'lg',
                mx: 'auto',
                px: { xs: 2, sm: 3 },
                pb: { xs: 2, sm: 4 },
                pt: { xs: 2, sm: 4 },
                overflowX: 'hidden'
            }}>
            {SHOW_CONNECTIONS_CAROUSEL ? <LandingConnectionsCarousel /> : null}

            {/* Sliding / Jigsaw / Word sections (featured + feature cards) */}
            <LandingFeaturedPuzzlesHero contentLanguage={contentLang} />

            {!user && <SignUpSection />}

            {/* Referral Event Section */}
            <ReferralEventSection />

            {/* Free Puzzles Section — shown for everyone (gated by SHOW_FREE_PUZZLES_SECTION) */}
            {SHOW_FREE_PUZZLES_SECTION && authReady && (
                <Box sx={{ mt: 6, mb: 6 }}>
                    <Typography
                        variant="h4"
                        gutterBottom
                        sx={{ textAlign: 'center', mb: 4, fontWeight: 600, fontSize: { xs: '1.5rem', sm: '1.65rem', md: '1.8rem' } }}
                    >
                        {t("landing.free-puzzles-title")}
                    </Typography>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ textAlign: 'center', mb: 4, maxWidth: 600, mx: 'auto', fontSize: { xs: '1.0625rem', sm: '1.125rem' } }}
                    >
                        {t("landing.free-puzzles-desc")}
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <ToggleButtonGroup
                            value={contentLang}
                            exclusive
                            onChange={handleContentLanguageChange}
                            size="small"
                            sx={{
                                '& .MuiToggleButton-root': {
                                    px: 2,
                                    py: 1,
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                    '&.Mui-selected': {
                                        backgroundColor: '#3BA666',
                                        color: '#fff',
                                        '&:hover': {
                                            backgroundColor: '#2e7d32',
                                            color: '#fff',
                                        },
                                    },
                                },
                            }}
                        >
                            <ToggleButton value="english" aria-label={t("landing.english")}>
                                {t("landing.english")}
                            </ToggleButton>
                            {SUPPORT_FRENCH ? (
                            <ToggleButton value="french" aria-label={t("landing.french")}>
                                {t("landing.french")}
                            </ToggleButton>
                            ) : null}
                        </ToggleButtonGroup>
                    </Box>

                    <Grid container spacing={3} sx={{ justifyContent: freePuzzles.length < 3 ? 'center' : undefined }}>
                        {freePuzzles.slice(0, 3).map((puzzle) => (
                            <Grid size={{ xs: 12, md: 4 }} key={puzzle.id}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: 4
                                        }
                                    }}
                                    onClick={() => handlePuzzleClick(puzzle)}
                                >
                                    <CardMedia
                                        component="div"
                                        sx={{
                                            height: 160,
                                            backgroundColor: '#e3f2fd',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative'
                                        }}
                                    >
                                        {puzzle.type === 'WORD_SEARCH' && <SearchIcon sx={{ fontSize: 48, color: '#3BA666' }} />}
                                        {puzzle.type === 'HIDDEN_MESSAGE' && <MessageIcon sx={{ fontSize: 48, color: '#3BA666' }} />}
                                        {puzzle.type === 'CROSSWORD' && <GridIcon sx={{ fontSize: 48, color: '#3BA666' }} />}

                                        <Chip
                                            label={t("landing.free")}
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                left: 8,
                                                fontWeight: 'bold'
                                            }}
                                        />

                                        <Chip
                                            label={
                                                puzzle.difficultyLevel === 'EASY' ? t("puzzle.card.difficulty-easy") :
                                                puzzle.difficultyLevel === 'MEDIUM' ? t("puzzle.card.difficulty-medium") :
                                                puzzle.difficultyLevel === 'HARD' ? t("puzzle.card.difficulty-hard") :
                                                puzzle.difficultyLevel
                                            }
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                backgroundColor:
                                                    puzzle.difficultyLevel === 'EASY' ? '#4caf50' :
                                                        puzzle.difficultyLevel === 'MEDIUM' ? '#ff9800' :
                                                            '#f44336',
                                                color: 'white',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    </CardMedia>
                                    <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                        {puzzle.template?.clusteredBubbles?.children?.length > 0 && (
                                            <Box
                                                sx={(theme) => ({
                                                    flexShrink: 0,
                                                    m: 0,
                                                    p: 0,
                                                    // Flush with card edges: cancel CardContent horizontal + top padding for this block
                                                    mx: theme.spacing(-2),
                                                    mt: theme.spacing(-2),
                                                    width: `calc(100% + ${theme.spacing(4)})`,
                                                })}
                                            >
                                                <ClusteredBubblesMiniChart
                                                    clusteredBubbles={puzzle.template.clusteredBubbles}
                                                />
                                            </Box>
                                        )}
                                        <Typography
                                            variant="h6"
                                            component="h3"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: { xs: '1.0625rem', sm: '1.125rem' },
                                                minHeight: '4.85rem',
                                                maxHeight: '4.85rem',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                mb: 2
                                            }}
                                        >
                                            {puzzle.title}
                                        </Typography>
                                        <Box sx={{ flexGrow: 1 }} />
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handlePuzzleClick(puzzle);
                                            }}
                                            sx={{
                                                backgroundColor: '#3BA666',
                                                '&:hover': {
                                                    backgroundColor: '#2e7d32'
                                                },
                                                textTransform: 'none',
                                                fontWeight: 600
                                            }}
                                        >
                                            {t("landing.play-now")}
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            {/* Benefits: SOLVE · REWARD · ASSIST · PROGRESS (gated by SHOW_BENEFITS_SECTION) */}
            {SHOW_BENEFITS_SECTION && (
            <Box sx={{ mb: 6 }}>
                <Typography
                    variant="h4"
                    gutterBottom
                    sx={{ textAlign: 'center', mb: 4, fontWeight: 600, fontSize: { xs: '1.5rem', sm: '1.65rem', md: '1.8rem' } }}
                >
                    {t("landing.benefits-title")}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Grid container spacing={3} sx={{ alignItems: 'stretch', width: '100%', m: 0 }}>
                        <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex' }}>
                            <Box sx={{ flex: 1, width: '100%', textAlign: 'center', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1.5, lineHeight: 1.3, fontSize: { xs: '1.0625rem', sm: '1.125rem' } }}>
                                    {t("landing.benefit-brain-title")}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.65, maxWidth: 420, mx: 'auto' }}>
                                    {t("landing.benefit-brain-desc")}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex' }}>
                            <Box sx={{ flex: 1, width: '100%', textAlign: 'center', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1.5, lineHeight: 1.3, fontSize: { xs: '1.0625rem', sm: '1.125rem' } }}>
                                    {t("landing.benefit-vocab-title")}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.65, maxWidth: 420, mx: 'auto' }}>
                                    {t("landing.benefit-vocab-desc")}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid container spacing={3} sx={{ alignItems: 'stretch', width: '100%', m: 0 }}>
                        <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex' }}>
                            <Box sx={{ flex: 1, width: '100%', textAlign: 'center', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1.5, lineHeight: 1.3, fontSize: { xs: '1.0625rem', sm: '1.125rem' } }}>
                                    {t("landing.benefit-stress-title")}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.65, maxWidth: 420, mx: 'auto' }}>
                                    {t("landing.benefit-stress-desc")}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex' }}>
                            <Box sx={{ flex: 1, width: '100%', textAlign: 'center', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1.5, lineHeight: 1.3, fontSize: { xs: '1.0625rem', sm: '1.125rem' } }}>
                                    {t("landing.benefit-quick-title")}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.65, maxWidth: 420, mx: 'auto' }}>
                                    {t("landing.benefit-quick-desc")}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
            )}

            {/* Why choose — 4 rows × 2 cards on md+, no horizontal scroll (gated by SHOW_WHY_CHOOSE_SECTION) */}
            {SHOW_WHY_CHOOSE_SECTION && (
            <Box sx={{
                backgroundColor: '#f5f5f5',
                borderRadius: 2,
                p: { xs: 3, sm: 4 },
                textAlign: 'center'
            }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 4, fontSize: { xs: '1.5rem', sm: '1.65rem', md: '1.8rem' } }}>
                    {t("landing.why-choose-title")}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 1100, mx: 'auto' }}>
                    {/* Featured row: puzzle catalog + AI Assistant */}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'stretch' }}>
                        {SUPPORT_FRENCH ? (
                        <Card
                            elevation={0}
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: 3,
                                overflow: 'hidden',
                                background: 'linear-gradient(180deg, #ffffff 0%, #f7fdfa 100%)',
                                border: '1px solid rgba(59, 166, 102, 0.22)',
                                boxShadow: '0 12px 44px rgba(27, 94, 32, 0.08), 0 4px 16px rgba(39, 97, 245, 0.07)',
                            }}
                        >
                            <Box sx={{ height: 5, width: '100%', background: 'linear-gradient(90deg, #2761F5 0%, #3BA666 100%)' }} />
                            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', pt: 2.5, pb: 2.5, px: { xs: 1.5, sm: 2 }, flexGrow: 1 }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 1, lineHeight: 1.3, textAlign: 'center', fontSize: { xs: '1.0625rem', sm: '1.15rem' }, color: '#1e3a2f' }}>
                                    {t("landing.whychoose-bilingual-title")}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, maxWidth: 440, mx: 'auto', textAlign: 'center' }}>
                                    {t("landing.whychoose-bilingual-desc")}
                                </Typography>
                                <WhyChooseVisualBilingualPuzzles showLangBadges />
                            </CardContent>
                        </Card>
                        ) : (
                        <Card
                            elevation={0}
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: 3,
                                overflow: 'hidden',
                                background: 'linear-gradient(180deg, #ffffff 0%, #f7fdfa 100%)',
                                border: '1px solid rgba(59, 166, 102, 0.22)',
                                boxShadow: '0 12px 44px rgba(27, 94, 32, 0.08), 0 4px 16px rgba(39, 97, 245, 0.07)',
                            }}
                        >
                            <Box sx={{ height: 5, width: '100%', background: 'linear-gradient(90deg, #2761F5 0%, #3BA666 100%)' }} />
                            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', pt: 2.5, pb: 2.5, px: { xs: 1.5, sm: 2 }, flexGrow: 1 }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 1, lineHeight: 1.3, textAlign: 'center', fontSize: { xs: '1.0625rem', sm: '1.15rem' }, color: '#1e3a2f' }}>
                                    {t("landing.whychoose-puzzles-title")}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, maxWidth: 440, mx: 'auto', textAlign: 'center' }}>
                                    {t("landing.whychoose-puzzles-desc")}
                                </Typography>
                                <WhyChooseVisualBilingualPuzzles showLangBadges={false} />
                            </CardContent>
                        </Card>
                        )}
                        <Card
                            elevation={0}
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: 3,
                                overflow: 'hidden',
                                background: 'linear-gradient(180deg, #ffffff 0%, #f8faff 100%)',
                                border: '1px solid rgba(39, 97, 245, 0.18)',
                                boxShadow: '0 12px 44px rgba(39, 97, 245, 0.09), 0 4px 16px rgba(59, 166, 102, 0.06)',
                            }}
                        >
                            <Box sx={{ height: 5, width: '100%', background: 'linear-gradient(90deg, #3BA666 0%, #2761F5 100%)' }} />
                            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', pt: 2.5, pb: 2.5, px: { xs: 1.5, sm: 2 }, flexGrow: 1 }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 1, lineHeight: 1.3, textAlign: 'center', fontSize: { xs: '1.0625rem', sm: '1.15rem' }, color: '#1e3a2f' }}>
                                    {t("landing.whychoose-assistant-title")}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, maxWidth: 440, mx: 'auto', textAlign: 'center' }}>
                                    {t("landing.whychoose-assistant-desc")}
                                </Typography>
                                <WhyChooseVisualAiAssistant />
                            </CardContent>
                        </Card>
                    </Box>
                    {/* Full width (2 columns): Story-first */}
                    {false && (
                    <Card
                        elevation={2}
                        sx={{
                            width: '100%',
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: 2,
                            overflow: 'visible',
                            border: '1px solid rgba(59, 166, 102, 0.2)',
                            boxShadow: '0 8px 28px rgba(27, 94, 32, 0.07), 0 2px 10px rgba(39, 97, 245, 0.06)',
                        }}
                    >
                        <Box sx={{ height: 4, width: '100%', background: 'linear-gradient(90deg, #2761F5 0%, #3BA666 50%, #2761F5 100%)' }} />
                        <CardContent sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            pt: 2.5,
                            pb: 2.5,
                            px: { xs: 1.5, sm: 2.5 },
                        }}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{
                                    fontWeight: 700,
                                    mb: 2,
                                    lineHeight: 1.3,
                                    textAlign: 'center',
                                    fontSize: { xs: '1.0625rem', sm: '1.2rem' },
                                    color: '#1e3a2f',
                                }}
                            >
                                {t("landing.whychoose-story-first-title")}
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                gap: { xs: 2.5, md: 4 },
                                alignItems: 'stretch',
                            }}>
                                <Box sx={{ flex: '1 1 0', minWidth: 0, textAlign: { xs: 'center', md: 'left' } }}>
                                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75, mb: 2 }}>
                                        {t("landing.whychoose-story-first-p1")}
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75, m: 0 }}>
                                        {t("landing.whychoose-story-first-p2")}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        flex: '1 1 0',
                                        minWidth: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignSelf: 'stretch',
                                        textAlign: { xs: 'center', md: 'left' },
                                    }}
                                >
                                    <Box
                                        component="ul"
                                        sx={{
                                            flex: '1 1 auto',
                                            minHeight: 0,
                                            m: 0,
                                            pl: { xs: 0, md: 2.5 },
                                            listStyle: 'none',
                                            '& li': {
                                                mb: 1.75,
                                                pl: 0,
                                                position: 'relative',
                                                '&:last-child': { mb: 0 },
                                            },
                                        }}
                                    >
                                        {[
                                            ['landing.whychoose-story-first-see-label', 'landing.whychoose-story-first-see-desc'],
                                            ['landing.whychoose-story-first-read-label', 'landing.whychoose-story-first-read-desc'],
                                            ['landing.whychoose-story-first-solve-label', 'landing.whychoose-story-first-solve-desc'],
                                        ].map(([labelKey, descKey]) => (
                                            <Box component="li" key={labelKey}>
                                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.65, m: 0 }}>
                                                    <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                                        {t(labelKey)}
                                                    </Box>{' '}
                                                    {t(descKey)}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            width: '100%',
                                            mt: 'auto',
                                            pt: 2,
                                        }}
                                    >
                                        <Button
                                            variant="contained"
                                            startIcon={<BookIcon sx={{ fontSize: 20 }} />}
                                            onClick={() => navigate('/puzzle-of-day')}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                px: 3,
                                                color: '#fff',
                                                backgroundColor: '#3BA666',
                                                boxShadow: 'none',
                                                '&:hover': {
                                                    backgroundColor: '#2e7d32',
                                                    boxShadow: 'none',
                                                },
                                            }}
                                        >
                                            {t('landing.puzzle-of-day-more')}
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                    )}
                    {/* Row 1: Word Networks + dictionary & translation */}
                    {/* <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'stretch' }}>
                        <Card elevation={2} sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', borderRadius: 2, overflow: 'visible' }}>
                            <CardContent sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                width: '100%',
                                maxWidth: '100%',
                                boxSizing: 'border-box',
                                pt: 2,
                                pb: 2.5,
                                px: { xs: 1, sm: 1.5 },
                                flexGrow: 1
                            }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1, lineHeight: 1.3, textAlign: 'center', fontSize: { xs: '1.0625rem', sm: '1.125rem' } }}>
                                    {t("landing.whychoose-item1-title")}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.65, maxWidth: 420, mx: 'auto', textAlign: 'center' }}>
                                    {t("landing.whychoose-item1-desc")}
                                </Typography>
                            </CardContent>
                        </Card>
                        <Card elevation={2} sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', borderRadius: 2, overflow: 'visible' }}>
                            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', pt: 2, pb: 2.5, flexGrow: 1 }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1, lineHeight: 1.3, fontSize: { xs: '1.0625rem', sm: '1.125rem' }, textAlign: 'center' }}>
                                    {t("landing.whychoose-item3-title")}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.65, maxWidth: 420, mx: 'auto', textAlign: 'center' }}>
                                    {t("landing.whychoose-item3-desc")}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box> */}
                    {/* Row 2: For Every Mind + Mastery */}
                    {/* <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'stretch' }}>
                        <Card elevation={2} sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', borderRadius: 2, overflow: 'visible' }}>
                            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2, pb: 2.5, flexGrow: 1 }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1, lineHeight: 1.3, fontSize: { xs: '1.0625rem', sm: '1.125rem' } }}>
                                    {t("landing.whychoose-item2-title")}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.65, maxWidth: 420, mx: 'auto' }}>
                                    {t("landing.whychoose-item2-desc")}
                                </Typography>
                            </CardContent>
                        </Card>
                        <Card elevation={2} sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', borderRadius: 2, overflow: 'visible' }}>
                            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2, pb: 2.5, flexGrow: 1 }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1, lineHeight: 1.3, fontSize: { xs: '1.0625rem', sm: '1.125rem' } }}>
                                    {t("landing.whychoose-item6-title")}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.65, maxWidth: 420, mx: 'auto' }}>
                                    {t("landing.whychoose-item6-desc")}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box> */}
                </Box>
            </Box>
            )}
            </Box>
        </Box>
    );
};

export default Landing;
